// Smart Sash Admin Panel - Lab Store (v2)
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { LabDTO, CreateLabRequest, UpdateLabRequest, PaginationMetadata } from '@/models';
import { labService } from '@/services/api/labService';
import { getErrorMessage } from '@/utils/getErrorMessage';

interface LabState {
  labs: LabDTO[];
  selectedLab: LabDTO | null;
  pagination: PaginationMetadata | null;
  isLoading: boolean;
  error: string | null;
  fetchLabs: (page?: number, pageSize?: number) => Promise<void>;
  fetchLabById: (institutionId: string, labId: string) => Promise<void>;
  createLab: (data: CreateLabRequest) => Promise<LabDTO>;
  updateLab: (institutionId: string, labId: string, data: UpdateLabRequest) => Promise<void>;
  deleteLab: (institutionId: string, labId: string) => Promise<void>;
  setSelectedLab: (lab: LabDTO | null) => void;
  clearError: () => void;
}

export const useLabStore = create<LabState>()(
  devtools((set) => ({
    labs: [],
    selectedLab: null,
    pagination: null,
    isLoading: false,
    error: null,
    fetchLabs: async (page = 1, pageSize = 10) => {
      set({ isLoading: true, error: null });
      try {
        const response = await labService.getAll({ page, pageSize });
        set({ labs: response.data, pagination: response.pagination, isLoading: false });
      } catch (error: unknown) {
        set({ error: getErrorMessage(error, 'Failed to fetch labs'), isLoading: false });
        throw error;
      }
    },
    fetchLabById: async (institutionId: string, labId: string) => {
      set({ isLoading: true, error: null });
      try {
        const lab = await labService.getById(institutionId, labId);
        set({ selectedLab: lab, isLoading: false });
      } catch (error: unknown) {
        set({ error: getErrorMessage(error, 'Failed to fetch lab'), isLoading: false });
        throw error;
      }
    },
    createLab: async (data: CreateLabRequest) => {
      set({ isLoading: true, error: null });
      try {
        const newLab = await labService.create(data);
        set((state) => ({ labs: [...state.labs, newLab], isLoading: false }));
        return newLab;
      } catch (error: unknown) {
        set({ error: getErrorMessage(error, 'Failed to create lab'), isLoading: false });
        throw error;
      }
    },
    updateLab: async (institutionId: string, labId: string, data: UpdateLabRequest) => {
      set({ isLoading: true, error: null });
      try {
        const updatedLab = await labService.update(institutionId, labId, data);
        set((state) => ({
          labs: state.labs.map((l) => (l.id === updatedLab.id ? updatedLab : l)),
          selectedLab:
            state.selectedLab?.id === updatedLab.id ? updatedLab : state.selectedLab,
          isLoading: false,
        }));
      } catch (error: unknown) {
        set({ error: getErrorMessage(error, 'Failed to update lab'), isLoading: false });
        throw error;
      }
    },
    deleteLab: async (institutionId: string, labId: string) => {
      set({ isLoading: true, error: null });
      try {
        await labService.delete(institutionId, labId);
        set((state) => ({
          labs: state.labs.filter((l) => l.id !== labId),
          selectedLab: state.selectedLab?.id === labId ? null : state.selectedLab,
          isLoading: false,
        }));
      } catch (error: unknown) {
        set({ error: getErrorMessage(error, 'Failed to delete lab'), isLoading: false });
        throw error;
      }
    },
    setSelectedLab: (lab: LabDTO | null) => set({ selectedLab: lab }),
    clearError: () => set({ error: null }),
  }), { name: 'LabStore' })
);
